import React, { useState, useEffect, useCallback } from 'react';
import {
  projectsAllApi,
  projectsPlanningApi,
  projectsTeamApi,
  projectsTaskTrackerApi,
  type ProjectDto,
  type PlanningItemDto,
  type ProjectTeamMemberDto,
  type TaskTrackerSprintDto,
  type TaskTrackerColumnDto,
  type TaskTrackerItemDto,
} from '../../services/api';
import CreateSprintModal from './CreateSprintModal';
import EditSprintModal from './EditSprintModal';
import AddTaskItemModal from './AddTaskItemModal';
import AddColumnModal from './AddColumnModal';
import { assignTasksByTeamComposition } from './taskTrackerAssignments';

const BACKLOG_SPRINT_NAME = 'Backlog';

export default function TaskTrackerPage() {
  const [projects, setProjects] = useState<ProjectDto[]>([]);
  const [sprints, setSprints] = useState<TaskTrackerSprintDto[]>([]);
  const [columns, setColumns] = useState<TaskTrackerColumnDto[]>([]);
  const [items, setItems] = useState<TaskTrackerItemDto[]>([]);
  const [planningItems, setPlanningItems] = useState<PlanningItemDto[]>([]);
  const [selectedProjectId, setSelectedProjectId] = useState<string>('');
  const [selectedSprintId, setSelectedSprintId] = useState<string>('');
  const [sprintModalOpen, setSprintModalOpen] = useState(false);
  const [editingSprint, setEditingSprint] = useState<TaskTrackerSprintDto | null>(null);
  const [taskModalOpen, setTaskModalOpen] = useState(false);
  const [columnModalOpen, setColumnModalOpen] = useState(false);
  const [editingItem, setEditingItem] = useState<TaskTrackerItemDto | null>(null);
  const [draggedItemId, setDraggedItemId] = useState<string | null>(null);
  const [dragOverColumnId, setDragOverColumnId] = useState<string | null>(null);
  const [teamMembers, setTeamMembers] = useState<ProjectTeamMemberDto[]>([]);
  const [assigningByTeam, setAssigningByTeam] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const loadProjects = useCallback(async () => {
    try {
      const res = await projectsAllApi.list();
      if (res.data) setProjects(res.data);
      else setProjects([]);
    } catch {
      setProjects([]);
    }
  }, []);

  const loadProjectData = useCallback(async (projectId: string) => {
    if (!projectId) return;
    try {
      const [sprintsRes, columnsRes, itemsRes, planningRes, teamRes] = await Promise.all([
        projectsTaskTrackerApi.listSprints(projectId),
        projectsTaskTrackerApi.listColumns(projectId),
        projectsTaskTrackerApi.listItems(projectId),
        projectsPlanningApi.list(projectId),
        projectsTeamApi.list(projectId),
      ]);
      if (sprintsRes.data) setSprints(sprintsRes.data);
      else setSprints([]);
      if (columnsRes.data) setColumns(columnsRes.data);
      else setColumns([]);
      if (itemsRes.data) setItems(itemsRes.data);
      else setItems([]);
      if (planningRes.data) setPlanningItems(planningRes.data);
      else setPlanningItems([]);
      if (teamRes.data) setTeamMembers(teamRes.data);
      else setTeamMembers([]);
    } catch {
      setSprints([]);
      setColumns([]);
      setItems([]);
      setPlanningItems([]);
      setTeamMembers([]);
    }
  }, []);

  useEffect(() => {
    loadProjects();
  }, [loadProjects]);

  useEffect(() => {
    if (selectedProjectId) {
      loadProjectData(selectedProjectId);
    } else {
      setSprints([]);
      setColumns([]);
      setItems([]);
      setPlanningItems([]);
      setTeamMembers([]);
    }
  }, [selectedProjectId, loadProjectData]);

  // Default to Backlog sprint when a project is selected and we have one
  useEffect(() => {
    if (!selectedProjectId || sprints.length === 0) return;
    const backlog = sprints.find((s) => s.project_id === selectedProjectId && s.name === BACKLOG_SPRINT_NAME);
    if (!backlog) return;
    const projectSprintIds = sprints.filter((s) => s.project_id === selectedProjectId).map((s) => s.id);
    const currentValid = selectedSprintId && projectSprintIds.includes(selectedSprintId);
    if (!currentValid) setSelectedSprintId(backlog.id);
  }, [selectedProjectId, sprints, selectedSprintId]);

  const projectColumns = selectedProjectId
    ? columns.filter((c) => c.project_id === selectedProjectId).sort((a, b) => a.sort_order - b.sort_order)
    : [];
  const backlogSprint = sprints.find((s) => s.project_id === selectedProjectId && s.name === BACKLOG_SPRINT_NAME);
  const projectSprints = selectedProjectId
    ? sprints.filter((s) => s.project_id === selectedProjectId).sort((a, b) => a.sort_order - b.sort_order)
    : [];
  const projectSprintsWithBacklog = backlogSprint
    ? [backlogSprint, ...projectSprints.filter((s) => s.id !== backlogSprint.id && s.name !== BACKLOG_SPRINT_NAME)]
    : projectSprints;
  const selectedSprint = projectSprintsWithBacklog.find((s) => s.id === selectedSprintId);
  const sprintItems = selectedSprintId
    ? items.filter((i) => i.sprint_id === selectedSprintId)
    : [];
  const projectPlanningItems = selectedProjectId
    ? planningItems
        .filter((p) => p.project_id === selectedProjectId)
        .sort((a, b) => a.sort_order - b.sort_order)
        .map((p) => ({ id: p.id, name: p.name }))
    : [];

  const handleSprintCreated = (sprint: TaskTrackerSprintDto) => {
    setSprints((prev) => [...prev, sprint]);
    setSelectedSprintId(sprint.id);
  };

  const handleColumnAdded = (col: TaskTrackerColumnDto) => {
    setColumns((prev) => [...prev, col]);
  };

  const handleTaskAdded = (item: TaskTrackerItemDto) => {
    setItems((prev) => [...prev, item]);
  };

  const handleTaskUpdated = (item: TaskTrackerItemDto) => {
    setItems((prev) => prev.map((i) => (i.id === item.id ? item : i)));
    setEditingItem(null);
  };

  const handleMoveItemToColumn = async (itemId: string, newColumnId: string) => {
    if (!selectedProjectId) return;
    try {
      const res = await projectsTaskTrackerApi.updateItem(selectedProjectId, itemId, { column_id: newColumnId });
      if (res.data) {
        setItems((prev) => prev.map((i) => (i.id === itemId ? res.data! : i)));
      }
    } catch {
      /* ignore */
    }
  };

  const openAddTask = () => {
    setEditingItem(null);
    setTaskModalOpen(true);
  };

  const openEditTask = (item: TaskTrackerItemDto) => {
    setEditingItem(item);
    setTaskModalOpen(true);
  };

  const handleSprintUpdated = (sprint: TaskTrackerSprintDto) => {
    setSprints((prev) => prev.map((s) => (s.id === sprint.id ? sprint : s)));
    setEditingSprint(null);
  };

  const handleDeleteSprint = async (sprintId: string) => {
    if (!selectedProjectId || !window.confirm('Delete this sprint and all its tasks?')) return;
    try {
      await projectsTaskTrackerApi.deleteSprint(selectedProjectId, sprintId);
      setSprints((prev) => prev.filter((s) => s.id !== sprintId));
      setItems((prev) => prev.filter((i) => i.sprint_id !== sprintId));
      if (selectedSprintId === sprintId) setSelectedSprintId('');
      await loadProjectData(selectedProjectId);
    } catch (err: unknown) {
      const msg = err && typeof err === 'object' && 'message' in err ? String((err as { message: string }).message) : 'Failed to delete sprint';
      window.alert(msg);
    }
  };

  const [movingToBacklog, setMovingToBacklog] = useState(false);
  const handleMoveIncompleteToBacklog = async () => {
    if (!selectedProjectId || !selectedSprintId || selectedSprint?.name === BACKLOG_SPRINT_NAME) return;
    setMovingToBacklog(true);
    try {
      const res = await projectsTaskTrackerApi.moveSprintIncompleteToBacklog(selectedProjectId, selectedSprintId);
      if (res.moved !== undefined && res.moved > 0) await loadProjectData(selectedProjectId);
    } finally {
      setMovingToBacklog(false);
    }
  };

  const handleEditColumn = async (colId: string) => {
    const col = projectColumns.find((c) => c.id === colId);
    if (!col || !selectedProjectId) return;
    const name = window.prompt('Column name', col.name);
    if (name != null && name.trim()) {
      try {
        const res = await projectsTaskTrackerApi.updateColumn(selectedProjectId, colId, { name: name.trim() });
        if (res.data) {
          setColumns((prev) => prev.map((c) => (c.id === colId ? res.data! : c)));
        }
      } catch {
        /* ignore */
      }
    }
  };

  const handleDeleteColumn = async (colId: string) => {
    const col = projectColumns.find((c) => c.id === colId);
    if (!col || !selectedProjectId || !window.confirm(`Remove column "${col.name}"? Tasks in it will be removed.`)) return;
    try {
      await projectsTaskTrackerApi.deleteColumn(selectedProjectId, colId);
      setColumns((prev) => prev.filter((c) => c.id !== colId));
      setItems((prev) => prev.filter((i) => i.column_id !== colId));
      await loadProjectData(selectedProjectId);
    } catch {
      /* ignore */
    }
  };

  const handleDeleteItem = async (itemId: string) => {
    if (!selectedProjectId) return;
    try {
      await projectsTaskTrackerApi.deleteItem(selectedProjectId, itemId);
      setItems((prev) => prev.filter((i) => i.id !== itemId));
    } catch {
      /* ignore */
    }
  };

  const handleAssignTask = async (itemId: string, assignedEmployeeId: string | null) => {
    if (!selectedProjectId) return;
    try {
      const res = await projectsTaskTrackerApi.updateItem(selectedProjectId, itemId, {
        assigned_employee_id: assignedEmployeeId ?? undefined,
      });
      if (res.data) {
        setItems((prev) => prev.map((i) => (i.id === itemId ? res.data! : i)));
      }
    } catch {
      /* ignore */
    }
  };

  const handleAssignByTeam = async () => {
    if (!selectedProjectId || !selectedSprintId || teamMembers.length === 0) return;
    const assignments = assignTasksByTeamComposition(
      teamMembers.map((m) => ({ employee_id: m.employee_id, employee_name: m.employee_name })),
      sprintItems,
      { onlyUnassigned: true }
    );
    if (assignments.length === 0) return;
    setAssigningByTeam(true);
    try {
      for (const { itemId, assigned_employee_id } of assignments) {
        const res = await projectsTaskTrackerApi.updateItem(selectedProjectId, itemId, {
          assigned_employee_id,
        });
        if (res.data) {
          setItems((prev) => prev.map((i) => (i.id === itemId ? res.data! : i)));
        }
      }
    } finally {
      setAssigningByTeam(false);
    }
  };

  const defaultColumnId = projectColumns[0]?.id ?? '';

  const handleRefresh = async () => {
    if (!selectedProjectId) return;
    setRefreshing(true);
    try {
      await loadProjectData(selectedProjectId);
    } finally {
      setRefreshing(false);
    }
  };

  // Drag and drop
  const onDragStart = (e: React.DragEvent, itemId: string) => {
    setDraggedItemId(itemId);
    e.dataTransfer.effectAllowed = 'move';
    e.dataTransfer.setData('text/plain', itemId);
  };

  const onDragOver = (e: React.DragEvent, columnId: string) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    setDragOverColumnId(columnId);
  };

  const onDragLeave = () => {
    setDragOverColumnId(null);
  };

  const onDrop = (e: React.DragEvent, columnId: string) => {
    e.preventDefault();
    setDragOverColumnId(null);
    setDraggedItemId(null);
    const itemId = e.dataTransfer.getData('text/plain');
    if (!itemId) return;
    const item = items.find((i) => i.id === itemId);
    if (item && item.column_id !== columnId) {
      void handleMoveItemToColumn(itemId, columnId);
    }
  };

  const onDragEnd = () => {
    setDraggedItemId(null);
    setDragOverColumnId(null);
  };

  return (
    <div className="max-w-7xl mx-auto w-full flex-1 min-h-0 flex flex-col overflow-hidden">
      <h1 className="text-lg font-bold text-gray-900 mb-3 shrink-0">Task Tracker</h1>
      <p className="text-sm text-gray-500 mb-4 shrink-0">
        Track tasks and progress across your projects. Create sprints, add tasks, and move them through columns (Todo → On-going → Completed).
      </p>

      <div className="flex flex-wrap items-end gap-4 mb-4 shrink-0">
        <div className="min-w-[200px]">
          <label className="block text-sm font-medium text-gray-700 mb-1">Project</label>
          <select
            value={selectedProjectId}
            onChange={(e) => {
              setSelectedProjectId(e.target.value);
              setSelectedSprintId('');
            }}
            className="w-full rounded-xl border border-gray-200 bg-white px-4 py-2.5 text-gray-900 focus:ring-2 focus:ring-brand-500/20 focus:border-brand-500 outline-none text-sm"
          >
            <option value="">— Choose a project —</option>
            {projects.map((p) => (
              <option key={p.id} value={p.id}>
                {p.project_name}
                {p.client_name ? ` (${p.client_name})` : ''}
              </option>
            ))}
          </select>
        </div>
        {selectedProjectId && (
          <>
            <div className="min-w-[200px]">
              <label className="block text-sm font-medium text-gray-700 mb-1">Sprint</label>
              <select
                value={selectedSprintId}
                onChange={(e) => setSelectedSprintId(e.target.value)}
                className="w-full rounded-xl border border-gray-200 bg-white px-4 py-2.5 text-gray-900 focus:ring-2 focus:ring-brand-500/20 focus:border-brand-500 outline-none text-sm"
              >
                <option value="">— Choose a sprint —</option>
                {projectSprintsWithBacklog.map((s) => (
                  <option key={s.id} value={s.id}>
                    {s.name === BACKLOG_SPRINT_NAME ? `${s.name} (incomplete items)` : s.name}
                  </option>
                ))}
              </select>
            </div>
            <button
              type="button"
              onClick={() => setSprintModalOpen(true)}
              className="inline-flex items-center gap-2 px-4 py-2.5 rounded-xl border border-gray-200 bg-white text-gray-700 hover:bg-gray-50 text-sm font-medium"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
              </svg>
              New Sprint
            </button>
            {selectedSprintId && selectedSprint?.name !== BACKLOG_SPRINT_NAME && (
              <>
                <button
                  type="button"
                  onClick={handleMoveIncompleteToBacklog}
                  disabled={movingToBacklog}
                  className="inline-flex items-center gap-2 px-4 py-2.5 rounded-xl border border-amber-200 bg-white text-amber-700 hover:bg-amber-50 text-sm font-medium disabled:opacity-50"
                  title="Move all tasks not in Completed to Backlog sprint"
                >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                  </svg>
                  {movingToBacklog ? 'Moving...' : 'Move incomplete to Backlog'}
                </button>
                <button
                  type="button"
                  onClick={() => setEditingSprint(selectedSprint ?? null)}
                  className="inline-flex items-center gap-2 px-4 py-2.5 rounded-xl border border-gray-200 bg-white text-gray-700 hover:bg-gray-50 text-sm font-medium"
                  title="Edit selected sprint"
                >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                  </svg>
                  Edit sprint
                </button>
                <button
                  type="button"
                  onClick={() => handleDeleteSprint(selectedSprintId)}
                  className="inline-flex items-center gap-2 px-4 py-2.5 rounded-xl border border-red-200 bg-white text-red-600 hover:bg-red-50 text-sm font-medium"
                  title="Delete selected sprint"
                >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                  </svg>
                  Delete sprint
                </button>
              </>
            )}
          </>
        )}
      </div>

      {projects.length === 0 && (
        <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 text-amber-800 text-sm">
          No projects yet. Create a project first in <strong>All Projects</strong>.
        </div>
      )}

      {selectedProjectId && !selectedSprintId && projectSprintsWithBacklog.length > 0 && (
        <div className="bg-sky-50 border border-sky-200 rounded-xl p-4 text-sky-800 text-sm">
          Select a sprint above to view and manage tasks.
        </div>
      )}

      {selectedProjectId && selectedSprintId && (
        <>
          <div className="flex justify-between items-center mb-3 shrink-0">
            <h2 className="font-semibold text-gray-900">
              {selectedSprint?.name ?? 'Sprint'} — Board
            </h2>
            <div className="flex items-center gap-2">
              <button
                type="button"
                onClick={() => void handleRefresh()}
                disabled={refreshing}
                className="inline-flex items-center gap-2 px-3 py-2 rounded-lg border border-gray-200 bg-white text-gray-600 hover:bg-gray-50 text-sm disabled:opacity-50 disabled:cursor-not-allowed"
                title="Refresh board to see latest employee updates"
              >
                {refreshing ? (
                  <>
                    <svg className="w-4 h-4 animate-spin" fill="none" viewBox="0 0 24 24" aria-hidden>
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                    </svg>
                    Refreshing…
                  </>
                ) : (
                  <>
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                    </svg>
                    Refresh
                  </>
                )}
              </button>
              {teamMembers.length > 0 && (
                <button
                  type="button"
                  onClick={() => void handleAssignByTeam()}
                  disabled={assigningByTeam || sprintItems.filter((i) => !i.assigned_employee_id).length === 0}
                  className="inline-flex items-center gap-2 px-3 py-2 rounded-lg border border-gray-200 bg-white text-gray-700 hover:bg-gray-50 text-sm disabled:opacity-50 disabled:cursor-not-allowed"
                  title="Assign unassigned tasks to team members (round-robin)"
                >
                  {assigningByTeam ? (
                    'Assigning…'
                  ) : (
                    <>
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                      </svg>
                      Assign by team
                    </>
                  )}
                </button>
              )}
              <button
                type="button"
                onClick={() => setColumnModalOpen(true)}
                className="inline-flex items-center gap-2 px-3 py-2 rounded-lg border border-gray-200 bg-white text-gray-600 hover:bg-gray-50 text-sm"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                </svg>
                Add column
              </button>
              <button
                type="button"
                onClick={openAddTask}
                className="inline-flex items-center gap-2 px-4 py-2.5 rounded-xl bg-brand-600 text-white text-sm font-semibold hover:bg-brand-700"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                </svg>
                Add task
              </button>
            </div>
          </div>

          <div className="flex-1 min-h-0 overflow-auto">
            <div className="flex gap-4 pb-4 min-w-max">
              {projectColumns.map((col) => {
                const colItems = sprintItems
                  .filter((i) => i.column_id === col.id)
                  .sort((a, b) => a.sort_order - b.sort_order);
                const isDropTarget = dragOverColumnId === col.id;
                return (
                  <div
                    key={col.id}
                    onDragOver={(e) => onDragOver(e, col.id)}
                    onDragLeave={onDragLeave}
                    onDrop={(e) => onDrop(e, col.id)}
                    className={`flex-shrink-0 w-72 rounded-xl border-2 transition-colors ${
                      isDropTarget ? 'border-brand-400 bg-brand-50/50' : 'border-gray-200 bg-gray-50/50'
                    }`}
                  >
                    <div className="p-3 border-b border-gray-200 flex items-center justify-between bg-white rounded-t-xl">
                      <div className="flex items-center gap-2">
                        <span className="font-semibold text-gray-900">{col.name}</span>
                        <span className="text-xs text-gray-500 bg-gray-100 px-2 py-0.5 rounded-full">
                          {colItems.length}
                        </span>
                      </div>
                      <div className="flex items-center gap-0.5">
                        <button
                          type="button"
                          onClick={() => handleEditColumn(col.id)}
                          className="p-1.5 rounded text-gray-400 hover:bg-gray-100 hover:text-gray-600"
                          title="Edit column"
                        >
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                          </svg>
                        </button>
                        {projectColumns.length > 1 && (
                          <button
                            type="button"
                            onClick={() => handleDeleteColumn(col.id)}
                            className="p-1.5 rounded text-gray-400 hover:bg-red-50 hover:text-red-600"
                            title="Delete column"
                          >
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                            </svg>
                          </button>
                        )}
                      </div>
                    </div>
                    <div className="p-2 min-h-[120px] space-y-2">
                      {colItems.map((item) => (
                        <div
                          key={item.id}
                          draggable
                          onDragStart={(e) => onDragStart(e, item.id)}
                          onDragEnd={onDragEnd}
                          className={`bg-white rounded-lg border border-gray-200 p-3 shadow-sm cursor-grab active:cursor-grabbing ${
                            draggedItemId === item.id ? 'opacity-50' : 'hover:border-brand-200'
                          }`}
                        >
                          <p className="font-medium text-gray-900 text-sm">{item.title}</p>
                          {item.description && (
                            <p className="text-xs text-gray-500 mt-1 line-clamp-2">{item.description}</p>
                          )}
                          {teamMembers.length > 0 && (
                            <div className="mt-1.5">
                              <label className="sr-only">Assign to</label>
                              <select
                                value={item.assigned_employee_id ?? ''}
                                onChange={(e) => void handleAssignTask(item.id, e.target.value || null)}
                                className="text-xs rounded border border-gray-200 px-2 py-1 text-gray-600 bg-white w-full"
                                onClick={(e) => e.stopPropagation()}
                                title="Assign to team member"
                              >
                                <option value="">Unassigned</option>
                                {teamMembers.map((m) => (
                                  <option key={m.employee_id} value={m.employee_id}>
                                    {m.employee_name}
                                  </option>
                                ))}
                              </select>
                            </div>
                          )}
                          <div className="flex items-center justify-between mt-2">
                            <select
                              value={item.column_id}
                              onChange={(e) => void handleMoveItemToColumn(item.id, e.target.value)}
                              className="text-xs rounded border border-gray-200 px-2 py-1 text-gray-600 bg-white"
                              onClick={(e) => e.stopPropagation()}
                            >
                              {projectColumns.map((c) => (
                                <option key={c.id} value={c.id}>
                                  Move to {c.name}
                                </option>
                              ))}
                            </select>
                            <div className="flex gap-0.5">
                              <button
                                type="button"
                                onClick={() => openEditTask(item)}
                                className="p-1.5 rounded text-gray-400 hover:bg-gray-100 hover:text-gray-600"
                                title="Edit"
                              >
                                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                                </svg>
                              </button>
                              <button
                                type="button"
                                onClick={() => {
                                  if (window.confirm('Delete this task?')) {
                                    void handleDeleteItem(item.id);
                                  }
                                }}
                                className="p-1.5 rounded text-gray-400 hover:bg-red-50 hover:text-red-600"
                                title="Delete"
                              >
                                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                                </svg>
                              </button>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          {selectedSprint && (
            <div className="mt-3 flex items-center gap-2 text-sm text-gray-500 shrink-0">
              <button
                type="button"
                onClick={() => handleDeleteSprint(selectedSprint.id)}
                className="text-red-600 hover:underline"
              >
                Delete this sprint
              </button>
            </div>
          )}
        </>
      )}

      {selectedProjectId && projectSprints.length === 0 && (
        <div className="bg-white rounded-xl p-8 border border-gray-100 shadow-card flex flex-col items-center justify-center text-center">
          <p className="font-medium text-gray-700">No sprints yet</p>
          <p className="text-sm text-gray-500 mt-1">Create a sprint to start adding tasks.</p>
          <button
            type="button"
            onClick={() => setSprintModalOpen(true)}
            className="mt-4 inline-flex items-center gap-2 px-4 py-2.5 rounded-xl bg-brand-600 text-white text-sm font-semibold hover:bg-brand-700"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
            </svg>
            Create first sprint
          </button>
        </div>
      )}

      <CreateSprintModal
        open={sprintModalOpen}
        onClose={() => setSprintModalOpen(false)}
        onSuccess={handleSprintCreated}
        projectId={selectedProjectId}
      />
      <EditSprintModal
        open={editingSprint != null}
        onClose={() => setEditingSprint(null)}
        onSuccess={handleSprintUpdated}
        sprint={editingSprint}
        projectId={selectedProjectId}
      />
      <AddTaskItemModal
        open={taskModalOpen}
        onClose={() => { setTaskModalOpen(false); setEditingItem(null); }}
        onSuccess={handleTaskAdded}
        sprintId={selectedSprintId}
        columns={projectColumns}
        defaultColumnId={defaultColumnId}
        planningItems={projectPlanningItems}
        projectId={selectedProjectId}
        editingItem={editingItem}
        onUpdate={handleTaskUpdated}
        onMoveItemToColumn={handleMoveItemToColumn}
        onDeleteItem={handleDeleteItem}
      />
      <AddColumnModal
        open={columnModalOpen}
        onClose={() => setColumnModalOpen(false)}
        onSuccess={handleColumnAdded}
        projectId={selectedProjectId}
      />
    </div>
  );
}
